import { createContext, useContext } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import { empresasService } from "app/services";
import { ErrorMessage, Spinner } from "app/components";

const ExportacionesContext = createContext();

export function ExportacionesProvider({ children }) {
  const params = useParams();
  const navigate = useNavigate();

  const exportacionesQuery = useQuery({
    queryKey: ["empresa:get:exportaciones", params.empresa_id],
    queryFn: () =>
      empresasService.getExportaciones({ empresaId: params.empresa_id }),
  });

  const exportacionAddMutation = useMutation({
    queryKey: ["empresa:add:exportacion", params.empresa_id],
    mutationFn: (data) =>
      toast.promise(empresasService.addExportacion(data), {
        loading: "Agregando la exportación...",
        success: (response) => response.statusText,
        error: (error) => error.response.statusText,
      }),
    onSuccess: async () => {
      navigate(".");

      return await exportacionesQuery.refetch();
    },
  });

  const exportacionUpdateMutation = useMutation({
    queryKey: ["empresa:update:exportacion"],
    mutationFn: (data) =>
      toast.promise(empresasService.updateExportacion(data), {
        loading: "Actualizando la exportación...",
        success: (response) => response.statusText,
        error: (error) => error.response.statusText,
      }),
    onSuccess: async () => {
      navigate(".");

      return await exportacionesQuery.refetch();
    },
  });

  const exportacionDeleteMutation = useMutation({
    queryKey: ["empresa:delete:exportacion"],
    mutationFn: (data) =>
      toast.promise(empresasService.deleteExportacion(data), {
        loading: "Eliminando la exportación...",
        success: (response) => response.statusText,
        error: (error) => error.response.statusText,
      }),
    onSuccess: async () => {
      navigate(".");

      return await exportacionesQuery.refetch();
    },
  });

  const exportacionVerifyMutation = useMutation({
    mutationKey: ["empresa:verify:exportacion"],
    mutationFn: (data) =>
      toast.promise(empresasService.verifyExportacion(data), {
        loading: "Verificando la exportación...",
        success: (response) => response.statusText,
        error: (error) => error.response.statusText,
      }),
    onSuccess: async () => {
      navigate(".");

      return await exportacionesQuery.refetch();
    },
  });

  async function addExportacion({ exportacion }) {
    return await exportacionAddMutation.mutateAsync({
      empresaId: params.empresa_id,
      exportacion,
    });
  }

  async function updateExportacion({ exportacionId, exportacion }) {
    const { exportacion_verificada, ...rest } = exportacion;

    return await exportacionUpdateMutation.mutateAsync({
      empresaId: params.empresa_id,
      exportacionId,
      exportacion: rest,
    });
  }

  async function deleteExportacion({ exportacionId }) {
    const deleted = window.confirm("¿Desea eliminar esta exportación?");

    if (!!deleted) {
      await exportacionDeleteMutation.mutateAsync({
        empresaId: params.empresa_id,
        exportacionId: exportacionId,
      });

      return true;
    }

    return false;
  }

  async function verifyExportacion({ exportacionId }) {
    const confirmed = window.confirm("¿Desea verificar esta exportación?");

    if (confirmed) {
      return await exportacionVerifyMutation.mutateAsync({
        empresaId: params.empresa_id,
        exportacionId,
      });
    }

    return false;
  }

  async function cancel() {
    if (window.confirm("¿Desea cancelar la edición?")) {
      return navigate(".");
    }
  }

  if (!!exportacionesQuery.isLoading) {
    return <Spinner label="Cargando las exportaciones..." />;
  } else if (!!exportacionesQuery.isError) {
    return (
      <ErrorMessage message={exportacionesQuery.error.response.statusText} />
    );
  }

  return (
    <ExportacionesContext.Provider
      value={{
        exportaciones: exportacionesQuery.data,
        isLoading: exportacionesQuery.isLoading,
        isFetching: exportacionesQuery.isFetching,
        isRefetching: exportacionesQuery.isRefetching,
        addExportacion,
        isAdding: exportacionAddMutation.isLoading,
        updateExportacion,
        isUpdating: exportacionUpdateMutation.isLoading,
        deleteExportacion,
        isDeleting: exportacionDeleteMutation.isLoading,
        verifyExportacion,
        isVerifing: exportacionVerifyMutation.isLoading,
        cancel,
      }}
    >
      {children}
    </ExportacionesContext.Provider>
  );
}

export function useExportacionesContext() {
  const exportacionesContext = useContext(ExportacionesContext);

  if (!exportacionesContext) {
    throw new Error(
      "useExportacionesContext should be used inside the ExportacionesProvider."
    );
  }

  return exportacionesContext;
}
